import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined'
import DownloadSharpIcon from '@mui/icons-material/DownloadSharp'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import HourglassTopOutlinedIcon from '@mui/icons-material/HourglassTopOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import _ from 'lodash'
import {
  FormProvider,
  RHFDateRangePickerField,
  RHFSearchableSelectField,
  RHFSelectField,
  RHFTextField,
} from '../../common/hook-form'
import RHFMultipleSelectField from '../../common/hook-form/RHFMultipleSelectField'
import { FieldLabel, FilterField, FilterDateField } from '../../common/FilterBar'
import { apiConstants, post } from '../../common/constants'
import { formatCurrency, formatDate } from '../../common/util'
import { convertDate } from '../../helpers/UtilityHelper'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import ProzoDataGrid from '../../common/ProzoDataGrid'
import PageTitle from '../../components/PageTitle'
import Loader from '../../common/Loader'
import CopyIcon from '../../common/CopyIcon'

const STATUS_OPTIONS = [
  { label: 'Pending', value: 'PENDING' },
  { label: 'Under Review', value: 'UNDER_REVIEW' },
  { label: 'Documents Pending', value: 'DOCUMENTS_PENDING' },
  { label: 'More Data Requested', value: 'MORE_DATA_REQUESTED' },
  { label: 'Data Uploaded', value: 'DATA_UPLOADED' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Partially Approved', value: 'PARTIALLY_APPROVED' },
  { label: 'Rejected', value: 'REJECTED' },
  { label: 'Closed', value: 'CLOSED' },
]

const CLAIM_TYPE_OPTIONS = [
  { label: 'Delivered Not Received', value: 'DNR' },
  { label: 'Damaged', value: 'DAMAGED' },
  { label: 'Wrong Item', value: 'WRONG_ITEM' },
  { label: 'Partial Delivery', value: 'PARTIAL' },
  { label: 'Empty Parcel', value: 'EMPTY' },
  { label: 'Never Picked', value: 'NEVER_PICKED' },
  { label: 'Lost Shipment', value: 'LOST' },
  { label: 'Handover Dispute', value: 'HANDOVER_DISPUTE' },
]

const SHIPMENT_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Forward', value: 'FORWARD' },
  { label: 'RVP', value: 'RVP' },
  { label: 'RTO', value: 'RTO' },
  { label: 'Handover', value: 'HANDOVER' },
]

const BRAND_TYPE_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Freight Only', value: 'FREIGHT_ONLY' },
  { label: 'E2E (Warehousing + Freight)', value: 'E2E' },
]

const statusColor = (s) => {
  switch (s) {
    case 'PENDING':
    case 'DOCUMENTS_PENDING':
    case 'MORE_DATA_REQUESTED':
      return 'warning'
    case 'UNDER_REVIEW':
    case 'DATA_UPLOADED':
      return 'info'
    case 'APPROVED':
    case 'PARTIALLY_APPROVED':
      return 'success'
    case 'CLOSED':
      return 'default'
    case 'REJECTED':
      return 'error'
    default:
      return 'primary'
  }
}

const prettyStatus = (s) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—'

const flatCardSx = {
  borderRadius: 2.5,
  border: '1px solid #e8e8e8',
  boxShadow: 'none',
}

function KpiTile({ icon, label, value, color, bg }) {
  return (
    <Box
      sx={{
        ...flatCardSx,
        backgroundColor: '#fff',
        p: 2,
        height: '100%',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          backgroundColor: bg,
          border: `1px solid ${color}25`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: '#9e9e9e',
            fontWeight: 600,
            fontSize: '0.68rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography
          sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#1a1a1a', lineHeight: 1.1, mt: 0.4 }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

const SectionHeader = ({ icon, title, count, action }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    {icon && <Box sx={{ display: 'flex', color: '#1565c0' }}>{icon}</Box>}
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>
      {title}
    </Typography>
    {count != null && (
      <Chip
        label={count}
        size="small"
        sx={{ height: 18, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#f0f4fa', color: '#1565c0' }}
      />
    )}
    {action && <Box sx={{ ml: 'auto' }}>{action}</Box>}
  </Stack>
)

function AllClaims() {
  const { enqueueSnackbar } = useSnackbar()

  const methods = useForm({
    defaultValues: {
      merchant: '',
      awb: '',
      status: [],
      claimType: '',
      shipmentType: '',
      brandType: '',
      fromDate: '',
      toDate: '',
    },
  })
  const { handleSubmit, reset } = methods

  const [rows, setRows] = useState([])
  const [page, setPage] = useState(0)
  const [sizePerPage, setSizePerPage] = useState(20)
  const [filters, setFilters] = useState({})
  const [loading, setLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)

  const { data: allMerchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()

  // Force the user to pick a specific merchant — no "All Merchants" option.
  const merchantOptions = useMemo(
    () =>
      _.orderBy(allMerchants, 'name').map((m) => ({ label: m.name, value: m.id })),
    [allMerchants]
  )

  const merchantNameById = useMemo(() => {
    const map = {}
    allMerchants.forEach((m) => { map[m.id] = m.name })
    return map
  }, [allMerchants])

  const getRowAwb = (row) =>
    row?.awb ||
    row?.awbNumber ||
    row?.proshipOnlyData?.proshipAwbNumber ||
    row?.orderData?.orderAwb ||
    ''

  useEffect(() => {
    if (!hasSearched) return
    setLoading(true)
    const body = {
      ...filters,
      skip: page * sizePerPage,
      limit: sizePerPage,
    }
    post(apiConstants.CLAIMS_LIST, body)
      .then((res) => {
        const list = res?.data?.responseObj || res?.data?.claims || res?.data || []
        setRows(
          (Array.isArray(list) ? list : []).map((r, i) => ({
            ...r,
            _gridId: r.claimId || r._id || r.id || `row-${i}-${r.awbNumber || r.awb || 'na'}`,
          }))
        )
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar('Failed to fetch claims', { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }, [filters, page, sizePerPage, hasSearched, enqueueSnackbar])

  const onSubmit = (values) => {
    if (!values.merchant) {
      enqueueSnackbar('Please select a merchant', { variant: 'warning' })
      return
    }
    setFilters({
      merchantId: values.merchant,
      awbNumber: values.awb || undefined,
      status: values.status?.length ? values.status : undefined,
      claimType: values.claimType || undefined,
      shipmentType: values.shipmentType || undefined,
      fromDate: values.fromDate ? convertDate(values.fromDate) : undefined,
      toDate: values.toDate ? convertDate(values.toDate) : undefined,
    })
    setPage(0)
    setHasSearched(true)
  }

  const onReset = () => {
    reset({
      merchant: '',
      awb: '',
      status: [],
      claimType: '',
      shipmentType: '',
      fromDate: '',
      toDate: '',
    })
    setRows([])
    setFilters({})
    setHasSearched(false)
    setPage(0)
  }

  const exportCsv = () => {
    if (!rows?.length) {
      enqueueSnackbar('No data to export — apply filters first', { variant: 'info' })
      return
    }
    const headers = [
      'Claim ID',
      'Merchant',
      'AWB',
      'Order ID',
      'Courier',
      'Claim Type',
      'Shipment Type',
      'Invoice Value',
      'Filed Date',
      'Status',
      'Aging (days)',
    ]
    const csv = [headers.join(',')]
      .concat(
        rows.map((r) => {
          const filed = r.filedAt || r.claimRegisteredDate
          const aging = filed ? Math.floor((Date.now() - new Date(filed).getTime()) / 86400000) : ''
          return [
            r.id || r.claimId || r._id,
            r.brandName || r.merchantName || merchantNameById[r.merchantId] || r.merchantId,
            getRowAwb(r),
            r.orderId || r.orderData?.orderId,
            r.courierCode || r.courierPartner || r.courierId,
            r.claimType,
            r.shipmentType,
            r.invoiceValue,
            filed ? formatDate(filed, { format: 'yyyy-MM-dd HH:mm' }) : '',
            r.claimStatus || r.status,
            aging,
          ]
            .map((v) => (v == null ? '' : String(v).replace(/"/g, '""')))
            .map((v) => (/[,"\n]/.test(v) ? `"${v}"` : v))
            .join(',')
        })
      )
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `claims_${formatDate(new Date(), { format: 'yyyyMMdd_HHmm' })}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  const kpis = useMemo(() => {
    const statusOf = (r) => r.claimStatus || r.status
    const total = rows.length
    const open = rows.filter((r) =>
      ['PENDING', 'UNDER_REVIEW', 'DOCUMENTS_PENDING', 'MORE_DATA_REQUESTED', 'DATA_UPLOADED'].includes(statusOf(r))
    ).length
    const approved = rows.filter((r) =>
      ['APPROVED', 'PARTIALLY_APPROVED'].includes(statusOf(r))
    ).length
    const rejected = rows.filter((r) => statusOf(r) === 'REJECTED').length
    return { total, open, approved, rejected }
  }, [rows])

  const columns = [
    {
      field: 'claimId',
      headerName: 'Claim ID',
      width: 230,
      renderCell: ({ row }) => {
        const id = row.id || row.claimId || row._id
        return id ? (
          <Link
            to={`/claims/${id}`}
            style={{ color: '#1565c0', textDecoration: 'none', fontWeight: 600, fontFamily: 'monospace' }}
          >
            {id}
          </Link>
        ) : '—'
      },
    },
    {
      field: 'brandName',
      headerName: 'Merchant',
      width: 200,
      renderCell: ({ row }) =>
        row.brandName || row.merchantName || merchantNameById[row.merchantId] || row.merchantId || '—',
    },
    {
      field: 'brandType',
      headerName: 'Brand Type',
      width: 130,
      renderCell: ({ row }) => {
        // TODO: Replace with actual brand classification from CT once available
        const mid = row.merchantId
        const merchant = allMerchants.find((m) => m.id === mid)
        const isE2E = merchant?.isD2CMerchant && merchant?.isPTLMerchant
        const label = isE2E ? 'E2E' : 'Freight Only'
        const color = isE2E ? 'info' : 'default'
        return <Chip size="small" label={label} color={color} variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
      },
    },
    {
      field: 'awb',
      headerName: 'AWB',
      width: 160,
      renderCell: ({ row }) => {
        const awb = getRowAwb(row)
        return awb ? (
          <Stack direction="row" alignItems="center" spacing={0}>
            <Link
              to={`/order-detail/${awb}`}
              style={{ color: '#1565c0', textDecoration: 'none', fontFamily: 'monospace' }}
            >
              {awb}
            </Link>
            <CopyIcon text={awb} />
          </Stack>
        ) : (
          '—'
        )
      },
    },
    {
      field: 'orderId',
      headerName: 'Order ID',
      width: 150,
      renderCell: ({ row }) => row.orderId || row.orderData?.orderId || '—',
    },
    {
      field: 'courier',
      headerName: 'Courier',
      width: 170,
      renderCell: ({ row }) => row.courierCode || row.courierPartner || row.courierId || '—',
    },
    {
      field: 'claimType',
      headerName: 'Claim Type',
      width: 200,
      renderCell: ({ row }) =>
        row.claimType ? (
          <Chip size="small" variant="outlined" label={row.claimType.replace(/_/g, ' ')} sx={{ height: 22 }} />
        ) : '—',
    },
    {
      field: 'shipmentType',
      headerName: 'Shipment',
      width: 110,
      renderCell: ({ row }) => row.shipmentType || '—',
    },
    {
      field: 'invoiceValue',
      headerName: 'Invoice Value',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }) =>
        row.invoiceValue != null ? formatCurrency(row.invoiceValue) : '—',
    },
    {
      field: 'filedAt',
      headerName: 'Filed Date',
      width: 170,
      renderCell: ({ row }) => {
        const d = row.filedAt || row.claimRegisteredDate
        return d ? formatDate(d, { format: 'dd MMM yyyy HH:mm' }) : '—'
      },
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 180,
      renderCell: ({ row }) => {
        const s = row.claimStatus || row.status
        return (
          <Chip
            size="small"
            color={statusColor(s)}
            label={prettyStatus(s)}
            sx={{ fontWeight: 500, height: 22 }}
          />
        )
      },
    },
    {
      field: 'aging',
      headerName: 'Aging',
      width: 100,
      renderCell: ({ row }) => {
        const d = row.filedAt || row.claimRegisteredDate
        if (!d) return '—'
        const hours = (Date.now() - new Date(d).getTime()) / 36e5
        const days = Math.floor(hours / 24)
        const status = row.claimStatus || row.status
        const isOpen = ['PENDING', 'UNDER_REVIEW', 'DOCUMENTS_PENDING'].includes(status)
        const breach = isOpen && hours > 72
        return (
          <Chip
            size="small"
            label={`${days}d`}
            color={breach ? 'error' : 'default'}
            variant={breach ? 'filled' : 'outlined'}
            sx={{ height: 22 }}
          />
        )
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => {
        const id = row.id || row.claimId || row._id
        return (
          <Tooltip title="View claim">
            <span>
              <IconButton
                component={Link}
                to={id ? `/claims/${id}` : '#'}
                disabled={!id}
                size="small"
                color="primary"
              >
                <VisibilityOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )
      },
    },
  ]

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {loading && <Loader />}
      <PageTitle>Claims</PageTitle>

      {/* ─── Page Header ─────────────────────────── */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ fontSize: '1.15rem', fontWeight: 800, color: '#1a1a1a' }}>
            All Claims
          </Typography>
          <Typography sx={{ color: '#6b6b6b', fontSize: '0.78rem', mt: 0.3 }}>
            Cross-merchant claim oversight. Filter, review and export claim records across Non-E2E merchants.
          </Typography>
        </Box>
        <Button
          type="button"
          startIcon={<DownloadSharpIcon />}
          variant="outlined"
          size="small"
          onClick={exportCsv}
          disabled={!rows?.length}
          sx={{ borderRadius: 1.5 }}
        >
          Export CSV
        </Button>
      </Stack>

      {/* ─── KPI Strip ─────────────────────────── */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <KpiTile
            icon={<AssignmentLateOutlinedIcon fontSize="small" />}
            label="Total (this view)"
            value={kpis.total}
            color="#1565c0"
            bg="#e3f2fd"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            icon={<HourglassTopOutlinedIcon fontSize="small" />}
            label="Open / Under Review"
            value={kpis.open}
            color="#e65100"
            bg="#fff3e0"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            label="Approved"
            value={kpis.approved}
            color="#2e7d32"
            bg="#e8f5e9"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            icon={<CancelOutlinedIcon fontSize="small" />}
            label="Rejected"
            value={kpis.rejected}
            color="#c62828"
            bg="#ffebee"
          />
        </Grid>
      </Grid>

      {/* ─── Filters Card ─────────────────────────── */}
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ ...flatCardSx, mb: 2 }}>
          <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

              <FilterField>
                <FieldLabel>Merchant</FieldLabel>
                <RHFSearchableSelectField
                  name="merchant"
                  label=""
                  options={merchantOptions}
                  fullWidth
                  noOptionsText="No merchants found"
                />
              </FilterField>

              <FilterField>
                <FieldLabel>AWB Number</FieldLabel>
                <RHFTextField name="awb" label="" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
              </FilterField>

              <FilterField>
                <FieldLabel>Status</FieldLabel>
                <RHFMultipleSelectField
                  name="status"
                  outputFormat="array"
                  options={STATUS_OPTIONS}
                  label=""
                  placeholder="Select Status"
                  width="100%"
                  size="small"
                />
              </FilterField>

              <FilterField>
                <FieldLabel>Claim Type</FieldLabel>
                <RHFSelectField
                  name="claimType"
                  label=""
                  placeholder="Select Claim Type"
                  options={[{ label: 'All', value: '' }, ...CLAIM_TYPE_OPTIONS]}
                  width="100%"
                  size="small"
                  displayEmpty
                />
              </FilterField>

              <FilterField>
                <FieldLabel>Shipment Type</FieldLabel>
                <RHFSelectField
                  name="shipmentType"
                  label=""
                  placeholder="Select Shipment Type"
                  options={SHIPMENT_TYPE_OPTIONS}
                  width="100%"
                  size="small"
                  displayEmpty
                />
              </FilterField>

              <FilterField>
                <FieldLabel>Brand Type</FieldLabel>
                <RHFSelectField
                  name="brandType"
                  label=""
                  placeholder="Select Brand Type"
                  options={BRAND_TYPE_OPTIONS}
                  width="100%"
                  size="small"
                  displayEmpty
                />
              </FilterField>

              <FilterDateField>
                <FieldLabel>Filed Date</FieldLabel>
                <RHFDateRangePickerField
                  name="filed_date"
                  label=""
                  placeholder="Select date range"
                  size="small"
                  maxDate={new Date()}
                />
              </FilterDateField>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                <Box component="button" type="submit"
                  sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#1565c0', color: '#fff', '&:hover': { bgcolor: '#0d47a1' } }}>
                  Filter
                </Box>
                <Box component="button" type="button" onClick={onReset}
                  sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: '20px', py: '8px', borderRadius: '6px', border: 'none', cursor: 'pointer', bgcolor: '#ef4444', color: '#fff', '&:hover': { bgcolor: '#dc2626' } }}>
                  Reset
                </Box>
              </Box>

            </Box>
          </CardContent>
        </Card>
      </FormProvider>

      {/* ─── Listing Card ─────────────────────────── */}
      <Card sx={flatCardSx}>
        <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
          <SectionHeader
            icon={<GavelOutlinedIcon fontSize="small" />}
            title="Claims List"
            count={hasSearched ? rows.length : null}
          />

          {!hasSearched ? (
            <Box
              sx={{
                p: 4,
                textAlign: 'center',
                borderRadius: 2,
                border: '1px dashed #d8dde6',
                bgcolor: '#fafbfd',
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: '#e3f2fd',
                  color: '#1565c0',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1.5,
                }}
              >
                <FilterAltOutlinedIcon fontSize="small" />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1a1a1a' }}>
                No filters applied yet
              </Typography>
              <Typography sx={{ color: '#6b6b6b', fontSize: '0.78rem', mt: 0.5 }}>
                Choose merchant, status or a date range above and click{' '}
                <strong>Apply Filters</strong> to load claims.
              </Typography>
            </Box>
          ) : (
            <ProzoDataGrid
              columns={columns}
              autoHeight
              rows={rows || []}
              sizePerPage={sizePerPage}
              setSizePerPage={setSizePerPage}
              setPage={setPage}
              page={page}
              pagination
              hideFooterPagination={false}
              filterMode="server"
              rowHeight={64}
              rowCount={Number.MAX_VALUE}
              getRowId={(row) => row._gridId}
              sx={{
                border: 0,
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f0f0f0',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: '#f7f9fc',
                  borderBottom: '1px solid #e8e8e8',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  color: '#1a1a1a',
                },
              }}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  )
}

export default AllClaims
